import CheckIcon from "@mui/icons-material/Check"
import {
  Autocomplete,
  Box,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material"
import { Controller, useFormContext, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"

import { StyledHelperText } from "@/components"
import { useUserCardsQuery, useValidateWtpQuery } from "@/queries/hooks"
import { LastUser } from "@/queries/hooks/useValidateWtpQuery"
import {
  isUserProfileCard,
  isWtpFormatValid,
  WTP_NUMBER_FORMAT,
} from "@/utils/wtpUtils"

import { useImageAutofill, useWtpNumberMask } from "./hooks"
import { PassHolderFormData } from "../../schema"
import { formatUserOption, formatWtpCardOption } from "../../utils"

type WtpNumberFieldProps = {
  usedWtpNumbers: string[]
  resortExternalId: number
}

const WtpNumberField = ({
  usedWtpNumbers,
  resortExternalId,
}: WtpNumberFieldProps) => {
  const { control, setValue, trigger, clearErrors } =
    useFormContext<PassHolderFormData>()
  const { t } = useTranslation()

  const wtpNumberMaskRef = useWtpNumberMask()

  const { data: userCards = [] } = useUserCardsQuery()

  const wtpNumber = useWatch({ control, name: "wtpNumber" }) || ""

  const isCardAdded = (wtpNumber: string) => {
    return usedWtpNumbers.includes(wtpNumber)
  }

  const isWtpUnvalidated = (wtpNumber: string) => {
    return !isUserProfileCard(userCards, wtpNumber) && !isCardAdded(wtpNumber)
  }

  const shouldTriggerAsyncValidation = (wtpNumber: string) => {
    return isWtpFormatValid(wtpNumber) && isWtpUnvalidated(wtpNumber)
  }

  const shouldEnableValidation = isWtpFormatValid(wtpNumber)

  const isProfileCard = isUserProfileCard(userCards, wtpNumber)

  const { data: validationData, isLoading: isValidatingWtpNumber } =
    useValidateWtpQuery(
      resortExternalId,
      wtpNumber,
      isProfileCard,
      shouldEnableValidation
    )

  useImageAutofill({
    lastUser: validationData?.lastUser,
    wtpNumber,
  })

  return (
    <Controller
      name="wtpNumber"
      control={control}
      render={({ field, fieldState: { error } }) =>
        userCards.length === 0 ? (
          <TextField
            inputRef={wtpNumberMaskRef}
            value={field.value || ""}
            onChange={(e) => {
              clearErrors("wtpNumber")
              const value = e.target.value.toUpperCase()
              field.onChange(value)
              if (shouldTriggerAsyncValidation(value)) {
                trigger("wtpNumber")
              }
            }}
            onBlur={() => {
              if (field.value) {
                trigger("wtpNumber")
              }
            }}
            name="wtpNumber"
            label={t("passHolderDetails.form.wtpNumber")}
            error={!!error}
            slotProps={{
              input: {
                endAdornment: isValidatingWtpNumber ? (
                  <Box
                    sx={{
                      transform: "translateY(-50%)",
                      display: "inline-flex",
                    }}
                  >
                    <CircularProgress size={24} />
                  </Box>
                ) : undefined,
              },
            }}
            helperText={
              <HelperText
                lastUser={
                  isWtpUnvalidated(wtpNumber)
                    ? validationData?.lastUser
                    : undefined
                }
                error={error?.message}
              />
            }
            placeholder={WTP_NUMBER_FORMAT}
          />
        ) : (
          <Autocomplete
            freeSolo
            disableClearable
            options={userCards}
            value={field.value}
            onBlur={() => {
              if (field.value) {
                trigger("wtpNumber")
              }
            }}
            onChange={(_, value) => {
              if (typeof value === "string") {
                return
              } else if (value) {
                setValue("wtpNumber", value.wtpNumber)
                setValue("firstName", value.firstName)
                setValue("lastName", value.lastName)
                setValue("birthDate", value.birthDate)
                clearErrors(["firstName", "lastName", "birthDate"])
              }
            }}
            onInputChange={(_, newInputValue, reason) => {
              clearErrors("wtpNumber")
              if (reason === "selectOption") {
                return
              }

              const wtpNumber = newInputValue.toUpperCase()

              field.onChange(wtpNumber)

              if (shouldTriggerAsyncValidation(wtpNumber)) {
                trigger("wtpNumber")
              }
            }}
            renderOption={(props, option) => {
              const { key, ...otherProps } = props
              const isAdded = isCardAdded(option.wtpNumber)
              return (
                <li key={key} {...otherProps}>
                  <Box
                    sx={{
                      display: "flex",
                      width: "100%",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1">
                        {formatUserOption(option.wtpNumber)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t("passHolderDetails.form.lastUsedBy")}:{" "}
                        {option.firstName} {option.lastName}
                      </Typography>
                    </Box>
                    {isAdded && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          ml: 2,
                        }}
                      >
                        <CheckIcon
                          sx={{ color: "success.main", fontSize: 20 }}
                        />
                        <Typography variant="body2">
                          {t("passHolderDetails.form.wtpNumberAdded")}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </li>
              )
            }}
            getOptionLabel={(option) => {
              return formatWtpCardOption(option)
            }}
            renderInput={(params) => (
              <TextField
                inputRef={wtpNumberMaskRef}
                {...params}
                name="wtpNumber"
                label={t("passHolderDetails.form.wtpNumber")}
                error={!!error}
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isValidatingWtpNumber && (
                          <Box
                            sx={{
                              transform: "translateY(-50%)",
                              display: "inline-flex",
                            }}
                          >
                            <CircularProgress size={24} />
                          </Box>
                        )}
                      </>
                    ),
                  },
                }}
                helperText={
                  <HelperText
                    lastUser={
                      isWtpUnvalidated(wtpNumber)
                        ? validationData?.lastUser
                        : undefined
                    }
                    error={error?.message}
                  />
                }
                placeholder={WTP_NUMBER_FORMAT}
              />
            )}
          />
        )
      }
    />
  )
}

const HelperText = ({
  lastUser,
  error,
}: {
  lastUser: LastUser | undefined
  error: string | undefined
}) => {
  const { t } = useTranslation()

  if (lastUser) {
    return (
      <StyledHelperText
        helperText={`${t("passHolderDetails.form.lastUsedBy")}: ${lastUser.firstName} ${lastUser.lastName}`}
        severity="info"
      />
    )
  }
  if (error) {
    return <StyledHelperText helperText={error} severity="error" />
  }
}

export default WtpNumberField
